import isEmpty from 'lodash/isEmpty';

import { isIntlValidLocale, toApiV2Language } from '@lumapps/translations';

/**
 * Construct an AI summary prompt
 * @param content the textual content that needs to be summarized
 * @param title the title that represent this content
 * @param targetLang the wanted summary language (use language code or full language name)
 * @returns the summary prompt to be used with getCompletions()
 */
export const getSummaryPrompt = (content: string, title?: string, targetLang?: string) => {
    /** Make sure that the taget lang is in V2 format. Legacy lang are not always supported by Intl API */
    const locale = targetLang && toApiV2Language(targetLang);

    if (isEmpty(content)) {
        return '';
    }
    const regionNamesInEnglish = new Intl.DisplayNames(['en'], { type: 'language' });

    const contentExample =
        "The History of Artificial Intelligence\n Artificial intelligence (AI) is the simulation of human intelligence processes by machines, especially computer systems. AI research has been highly successful in developing effective techniques for solving a wide range of problems, from game playing to medical diagnosis. The Early Days of AI The history of AI can be traced back to the early days of computing. In the 1940s and 1950s, a number of scientists and engineers began to explore the possibility of creating machines that could think for themselves. Among the pioneers of AI were Alan Turing, John von Neumann, and Norbert Wiener. In 1950, Turing published a paper entitled 'Computing Machinery and Intelligence,' in which he proposed a test for determining whether a machine is intelligent. The Turing test involves a human interrogator who converses with a machine and a human being, both of whom are hidden from the interrogator. If the interrogator cannot reliably distinguish between the machine and the human, then the machine is said to have passed the Turing test.";
    const contentSummaryExample =
        "AI simulates human intelligence in machines. Early exploration in the 1940s-50s by pioneers like Turing, von Neumann, and Wiener led to Turing's 1950 paper proposing the Turing test to assess machine intelligence.";
    let prompt = `Generate a comprehensive and short summary of my content in less than 500 characters, do not write the content title again, do not tell me it's a summary and never ever talk to me about the provided example. The content can contains multiple language, use all of them to make a summary. Do not use markdown, list and table. Only output the plain text summary without any additional information, explication or comment. Your output will be directly used as a summary. If there is not enough information to summarize, output "Not enough information to summarize" in the ${
        locale && isIntlValidLocale(locale) ? `'${regionNamesInEnglish.of(locale)}'` : 'correct'
    } language. I will give you an example, then you will have to summarize the content I will provide. It's really important to use only the information provided by the content, and not the given example about The History of Artificial Intelligence.`;

    if (locale && isIntlValidLocale(locale)) {
        prompt = prompt.concat(
            '\n',
            `Make sure to return the summary in the '${regionNamesInEnglish.of(locale)}' language.`,
        );
    }

    const examplePrompt = `### Example Title: The History of Artificial Intelligence\n${contentExample}\n### Summary\n${contentSummaryExample}`;
    prompt = prompt.concat('\n', examplePrompt);

    if (title) {
        prompt = prompt.concat('\n', `### Title: ${title}`);
    } else {
        prompt = prompt.concat('\n', '### Content');
    }

    prompt = prompt.concat('\n', `${content.replace(/\n|\r/g, '')}\n### Summary\n`);

    return prompt;
};
